import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Stack, LinearProgress,
  Button, Chip, Paper, Avatar
} from '@mui/material';
import {
  Assignment as ProjectIcon, CheckCircleOutline as DoneIcon,
  AttachMoney as MoneyIcon, Flag as MilestoneIcon,
  Add as AddIcon, ArrowForward as ArrowForwardIcon,
  PlayArrow as PlayArrowIcon, ListAlt as ListAltIcon,
  Warning as WarningIcon, Tune as TuneIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage from '../../Commun/components/GenericPage';
import blobticService from '../../Commun/services/blobticService';
import { projectApi } from '../services/projectApi';
import { PrjDashboardStats, PrjProject, PrjTask } from '../types/project';

const DEFAULT_PRJ_CARDS = [
  { name: 'totalProjects', title: 'Total Projets', translationKey: 'project.dashboard.kpi.total_projects', desc: 'project.dashboard.kpi.desc.total_projects', order: 1, color: 'it-indigo', icon: 'Assignment', type: 'card', span: { sm: 6, md: 3, lg: 3 } },
  { name: 'activeProjects', title: 'Projets en Cours', translationKey: 'project.dashboard.kpi.active_projects', desc: 'project.dashboard.kpi.desc.active_projects', order: 2, color: 'it-blue', icon: 'PlayArrow', type: 'card', span: { sm: 6, md: 3, lg: 3 } },
  { name: 'totalTasks', title: 'Tâches Totales', translationKey: 'project.dashboard.kpi.total_tasks', desc: 'project.dashboard.kpi.desc.total_tasks', order: 3, color: 'it-purple', icon: 'ListAlt', type: 'card', span: { sm: 6, md: 3, lg: 3 } },
  { name: 'doneTasks', title: 'Tâches Terminées', translationKey: 'project.dashboard.kpi.done_tasks', desc: 'project.dashboard.kpi.desc.done_tasks', order: 4, color: 'it-emerald', icon: 'CheckCircle', type: 'card', span: { sm: 6, md: 3, lg: 3 } },
  { name: 'overdueTasks', title: 'Tâches en Retard', translationKey: 'project.dashboard.kpi.overdue_tasks', desc: 'project.dashboard.kpi.desc.overdue_tasks', order: 5, color: 'it-red', icon: 'Warning', type: 'card', span: { sm: 6, md: 3, lg: 3 } },
  { name: 'totalBudget', title: 'Budget Global', translationKey: 'project.dashboard.kpi.total_budget', desc: 'project.dashboard.kpi.desc.total_budget', order: 6, color: 'it-teal', icon: 'AttachMoney', type: 'card', span: { sm: 6, md: 3, lg: 3 } },
  { name: 'totalSpent', title: 'Budget Consommé', translationKey: 'project.dashboard.kpi.total_spent', desc: 'project.dashboard.kpi.desc.total_spent', order: 7, color: 'it-amber', icon: 'AttachMoney', type: 'card', span: { sm: 6, md: 3, lg: 3 } },
  { name: 'pendingMilestones', title: 'Jalons Clés', translationKey: 'project.dashboard.kpi.pending_milestones', desc: 'project.dashboard.kpi.desc.pending_milestones', order: 8, color: 'it-cyan', icon: 'Flag', type: 'card', span: { sm: 6, md: 3, lg: 3 } }
];

const COLOR_STYLES: Record<string, { bg: string; text: string; iconBg: string; border: string }> = {
  'it-indigo': { bg: '#eef2ff', text: '#4f46e5', iconBg: '#e0e7ff', border: '#c7d2fe' },
  'it-blue': { bg: '#eff6ff', text: '#0284c7', iconBg: '#e0f2fe', border: '#bae6fd' },
  'it-purple': { bg: '#f5f3ff', text: '#7c3aed', iconBg: '#ede9fe', border: '#ddd6fe' },
  'it-cyan': { bg: '#ecfeff', text: '#0891b2', iconBg: '#cffafe', border: '#a5f3fc' },
  'it-emerald': { bg: '#ecfdf5', text: '#059669', iconBg: '#d1fae5', border: '#a7f3d0' },
  'it-teal': { bg: '#f0fdfa', text: '#0d9488', iconBg: '#ccfbf1', border: '#99f6e4' },
  'it-amber': { bg: '#fffbeb', text: '#d97706', iconBg: '#fef3c7', border: '#fde68a' },
  'it-orange': { bg: '#fff7ed', text: '#ea580c', iconBg: '#ffedd5', border: '#fed7aa' },
  'it-red': { bg: '#fef2f2', text: '#dc2626', iconBg: '#fee2e2', border: '#fecaca' },
  'it-green': { bg: '#f0fdf4', text: '#16a34a', iconBg: '#dcfce7', border: '#bbf7d0' },
};

export const DashboardPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [stats, setStats] = useState<PrjDashboardStats>({
    totalProjects: 0,
    activeProjects: 0,
    totalTasks: 0,
    doneTasks: 0,
    overdueTasks: 0,
    totalBudget: 0,
    totalSpent: 0,
    pendingMilestones: 0,
  });
  const [kpiList, setKpiList] = useState<any[]>(DEFAULT_PRJ_CARDS);
  const [recentProjects, setRecentProjects] = useState<PrjProject[]>([]);
  const [recentTasks, setRecentTasks] = useState<PrjTask[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsData, projectsData, tasksData] = await Promise.all([
          projectApi.getDashboardStats(),
          projectApi.getProjects(),
          projectApi.getTasks(),
        ]);
        setStats(statsData);
        setRecentProjects(projectsData.slice(0, 5));
        setRecentTasks(tasksData.slice(0, 6));

        // Load parameterized KPI configuration for current user's profile
        try {
          const profilRes = await blobticService.executeZoomRequest<any>({
            ZoomKey: 'config_kpi',
            Action: { OKey: 'Blobtic_Sys_Profil,config_kpi,get_profil_kpi', APP_NAME: 'ProjectManagement' }
          });
          let customList: any[] = [];
          if (profilRes.success && profilRes.data) {
            const raw = Array.isArray(profilRes.data) ? profilRes.data[0] : profilRes.data;
            if (raw?.kpiJson) customList = JSON.parse(raw.kpiJson);
          }
          if (!customList || customList.length === 0) {
            const defRes = await blobticService.executeZoomRequest<any>({
              ZoomKey: 'config_kpi',
              Action: { OKey: 'Blobtic_Sys_Profil,config_kpi,get_default_kpi', APP_NAME: 'ProjectManagement' }
            });
            if (defRes.success && defRes.data) {
              const rawDef = Array.isArray(defRes.data) ? defRes.data[0] : defRes.data;
              if (rawDef?.kpiJson) customList = JSON.parse(rawDef.kpiJson);
            }
          }
          if (customList && customList.length > 0) {
            setKpiList(customList.filter((k: any) => k.enabled !== false));
          }
        } catch (e) {
          console.warn('Could not load ProjectManagement dynamic KPI configuration, using defaults:', e);
        }
      } catch (err) {
        console.error('Error fetching ProjectManagement dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const taskCompletionRate = stats.totalTasks > 0
    ? Math.round((stats.doneTasks / stats.totalTasks) * 100)
    : 0;

  const budgetUsageRate = stats.totalBudget > 0
    ? Math.round((stats.totalSpent / stats.totalBudget) * 100)
    : 0;

  const getKpiIcon = (iconName?: string) => {
    switch (iconName) {
      case 'Assignment': return <ProjectIcon />;
      case 'PlayArrow': return <PlayArrowIcon />;
      case 'ListAlt': return <ListAltIcon />;
      case 'CheckCircle': return <DoneIcon />;
      case 'Warning': return <WarningIcon />;
      case 'AttachMoney': return <MoneyIcon />;
      case 'Flag': return <MilestoneIcon />;
      default: return <ProjectIcon />;
    }
  };

  const getKpiRoute = (name: string) => {
    if (name === 'totalProjects' || name === 'activeProjects' || name === 'totalBudget' || name === 'totalSpent') return '/ProjectManagement/projects';
    if (name === 'totalTasks' || name === 'doneTasks' || name === 'overdueTasks') return '/ProjectManagement/tasks';
    if (name === 'pendingMilestones') return '/ProjectManagement/milestones';
    return '/ProjectManagement/projects';
  };

  const getKpiValue = (name: string) => {
    switch (name) {
      case 'totalProjects': return stats.totalProjects;
      case 'activeProjects': return stats.activeProjects;
      case 'totalTasks': return stats.totalTasks;
      case 'doneTasks': return stats.doneTasks;
      case 'overdueTasks': return stats.overdueTasks;
      case 'totalBudget': return `${stats.totalBudget.toLocaleString()} DZD`;
      case 'totalSpent': return `${stats.totalSpent.toLocaleString()} DZD`;
      case 'pendingMilestones': return stats.pendingMilestones;
      default: return 0;
    }
  };

  const getKpiBadge = (name: string) => {
    switch (name) {
      case 'totalProjects': return `${stats.activeProjects} ${t('project.dashboard.active', 'actifs')}`;
      case 'activeProjects': return t('project.dashboard.inProgress', 'En production');
      case 'totalTasks': return `${taskCompletionRate}% ${t('project.dashboard.completedRate', 'complétées')}`;
      case 'doneTasks': return `${stats.doneTasks} / ${stats.totalTasks}`;
      case 'overdueTasks': return stats.overdueTasks > 0 ? t('project.dashboard.actionNeeded', 'Action requise') : t('project.dashboard.onTrack', 'À jour');
      case 'totalBudget': return t('project.dashboard.budgetAllocated', 'Enveloppe allouée');
      case 'totalSpent': return `${budgetUsageRate}% ${t('project.dashboard.consumed', 'consommé')}`;
      case 'pendingMilestones': return t('project.dashboard.upcomingMilestones', 'À valider');
      default: return '';
    }
  };

  const sortedCards = [...kpiList]
    .filter((k: any) => k.type !== 'widget')
    .sort((a: any, b: any) => (a.order ?? 99) - (b.order ?? 99));

  return (
    <GenericPage
      title={t('project.dashboard.title', 'Tableau de bord - Gestion de Projet')}
      icon={<ProjectIcon />}
      footerEnabled={false}
      viewModeEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            color="primary"
            startIcon={<TuneIcon />}
            onClick={() => navigate('/bmsyu_config_kpi?app=ProjectManagement')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {t('project.dashboard.customizeKpi', 'Paramétrer les KPIs')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/ProjectManagement/projects')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {t('project.projects.new', 'Nouveau Projet')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate('/ProjectManagement/tasks')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {t('project.tasks.new', 'Nouvelle Tâche')}
          </Button>
        </Stack>
      }
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Parameterized KPI Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {sortedCards.map((card: any) => {
          const style = COLOR_STYLES[card.color] || COLOR_STYLES['it-indigo'];
          const span = card.span || {};
          const smSpan = span.sm ?? 6;
          const mdSpan = span.md ?? 3;
          const lgSpan = span.lg ?? 3;
          const badgeText = getKpiBadge(card.name);

          return (
            <Grid item xs={12} sm={smSpan} md={mdSpan} lg={lgSpan} key={card.name}>
              <Card
                onClick={() => navigate(getKpiRoute(card.name))}
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 25px rgba(0,0,0,0.09)' }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500} noWrap>
                        {card.translationKey ? t(card.translationKey, card.title) : card.title}
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ my: 0.5, color: '#0f172a' }}>
                        {getKpiValue(card.name)}
                      </Typography>
                      {badgeText && (
                        <Chip
                          label={badgeText}
                          size="small"
                          sx={{ mt: 0.5, bgcolor: style.bg, color: style.text, fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                    <Avatar sx={{ bgcolor: style.iconBg, color: style.text, width: 48, height: 48 }}>
                      {getKpiIcon(card.icon)}
                    </Avatar>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Main Content: Projects Progress & Tasks Overview */}
      <Grid container spacing={3}>
        {/* Active Projects Overview */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                {t('project.dashboard.recentProjects', 'Projets en cours')}
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/ProjectManagement/projects')}
              >
                {t('common.viewAll', 'Voir tout')}
              </Button>
            </Stack>
            <Stack spacing={2}>
              {recentProjects.map((p) => {
                const isOverBudget = p.budget > 0 && p.spent > p.budget;
                return (
                  <Box
                    key={p.id}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {p.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.client ? `${p.client} • ` : ''}{p.code}
                        </Typography>
                      </Box>
                      <Chip
                        label={p.status}
                        size="small"
                        color={p.status === 'COMPLETED' ? 'success' : p.status === 'IN_PROGRESS' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={p.progress || 0}
                          color={p.progress === 100 ? 'success' : 'primary'}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                      <Typography variant="caption" fontWeight={600}>
                        {p.progress || 0}%
                      </Typography>
                      {isOverBudget && (
                        <Chip
                          label={t('project.dashboard.overBudget', 'Dépassement')}
                          size="small"
                          color="error"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                    </Stack>
                  </Box>
                );
              })}
              {recentProjects.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {t('project.dashboard.noProjects', 'Aucun projet pour le moment.')}
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Recent Urgent Tasks */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                {t('project.dashboard.recentTasks', 'Tâches Récentes')}
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/ProjectManagement/tasks')}
              >
                {t('common.viewAll', 'Voir tout')}
              </Button>
            </Stack>
            <Stack spacing={1.5}>
              {recentTasks.map((task) => (
                <Box
                  key={task.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {task.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.dueDate ? `Échéance : ${task.dueDate}` : task.code}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={task.priority}
                      size="small"
                      color={task.priority === 'HIGH' || task.priority === 'CRITICAL' ? 'error' : 'default'}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                    <Chip
                      label={task.status}
                      size="small"
                      color={task.status === 'DONE' ? 'success' : task.status === 'IN_PROGRESS' ? 'info' : 'default'}
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Stack>
                </Box>
              ))}
              {recentTasks.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {t('project.dashboard.noTasks', 'Aucune tâche enregistrée.')}
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </GenericPage>
  );
};

export default DashboardPage;
